import { storeToRefs } from "pinia"
import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import type { IProductionStatementCustomerPurchaseOrder } from "@/interfaces/api/production-statement-customer-purchase-order.interface"
import { ProductionStatementCustomerPurchaseOrder } from "@/models/production-statement-customer-purchase-order.model"
import { ProductionStatementCustomerPurchaseOrderRepository } from "@/repositories/production-statement-customer-purchase-order-repository"
import { useCustomerPurchaseOrderStore } from "@/store/customer-purchase-orders.store"
import { useProductionStatementStore } from "@/store/production-statements.store"
import { useRelatedObjectsManager } from "@/composables/socket/related-objects-manager"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers"
import type { CustomerPurchaseOrder } from "@/models/customer-purchase-order.model"
import type { ProductionStatement } from "@/models/production-statement.model"

export class ProductionStatementCustomerPurchaseOrderController extends GenericModelController<
  ProductionStatementCustomerPurchaseOrder,
  IProductionStatementCustomerPurchaseOrder,
  ProductionStatementCustomerPurchaseOrderRepository
> {
  constructor() {
    const customerPurchaseOrderStore = useCustomerPurchaseOrderStore()
    const productionStatementStore = useProductionStatementStore()
    const { customerPurchaseOrders } = storeToRefs(customerPurchaseOrderStore)
    const { productionStatements } = storeToRefs(productionStatementStore)

    const {
      pushToRelatedObjects: pushToCustomerPurchaseOrder,
      removeFromRelatedObjects: removeFromCustomerPurchaseOrder
    } = useRelatedObjectsManager(
      customerPurchaseOrders,
      (allocation: ProductionStatementCustomerPurchaseOrder) => allocation.customerPurchaseOrder,
      (customerPurchaseOrder: CustomerPurchaseOrder) => customerPurchaseOrder.id,
      (customerPurchaseOrder: CustomerPurchaseOrder) =>
        customerPurchaseOrder.productionStatementCustomerPurchaseOrders
    )
    const {
      pushToRelatedObjects: pushToProductionStatement,
      removeFromRelatedObjects: removeFromProductionStatement
    } = useRelatedObjectsManager(
      productionStatements,
      (allocation: ProductionStatementCustomerPurchaseOrder) => allocation.productionStatement,
      (productionStatement: ProductionStatement) => productionStatement.id,
      (productionStatement: ProductionStatement) =>
        productionStatement.productionStatementCustomerPurchaseOrders
    )

    function pushToRelatedObjects(allocation: ProductionStatementCustomerPurchaseOrder) {
      pushToCustomerPurchaseOrder(allocation)
      pushToProductionStatement(allocation)
    }

    function removeFromRelatedObjects(allocation: ProductionStatementCustomerPurchaseOrder) {
      removeFromCustomerPurchaseOrder(allocation)
      removeFromProductionStatement(allocation)
    }

    super(
      ProductionStatementCustomerPurchaseOrder,
      {
        onCreate: pushToRelatedObjects,
        onDelete: removeFromRelatedObjects
      },
      new ProductionStatementCustomerPurchaseOrderRepository()
    )

    this.socketSynchronizationHandlers = useSocketSynchronizationHandlers(
      ProductionStatementCustomerPurchaseOrder,
      null,
      pushToRelatedObjects,
      removeFromRelatedObjects
    )
  }
}
